import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  IconButton,
  Menu,
  ListItemIcon,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  TrendingUp,
  TrendingDown,
  Visibility,
  Delete as DeleteIcon,
  Remove as RemoveIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  DriveFileMove as MoveIcon,
  Dashboard as DashboardIcon,
  DashboardOutlined as DashboardOutlinedIcon,
  Assessment,
} from '@mui/icons-material';
import { useAppContext } from '../context/AppContext';
import { useStockData } from '../context/StockDataContext';
import { WatchlistGroup, Stock } from '../types';
import { WatchlistService } from '../services/watchlistService';
import PageHeader from './common/PageHeader';
import GroupSelectorDialog from './common/GroupSelectorDialog';
import DividendDisplay from './DividendDisplay';
import { useNotification } from '../context/NotificationContext';
import { StockNameManager } from '../services/stockNameManager';
import {
  getTrendChipProps,
  getTrendDirection,
  getTrendTextColor,
} from '../theme/taiwanTrendColors';

const Watchlist: React.FC = () => {
  const { state, updateWatchlistData } = useAppContext();
  const { showError, showSuccess, showWarning } = useNotification();
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [selectedStock, setSelectedStock] = useState<Stock | null>(null);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedGroupForAction, setSelectedGroupForAction] = useState<string | null>(null);
  const [selectedStockForAction, setSelectedStockForAction] = useState<{stock: Stock, groupId: string | null} | null>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'deleteGroup' | 'removeStock' | null>(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [tempGroupName, setTempGroupName] = useState('');
  const [moveStockDialogOpen, setMoveStockDialogOpen] = useState(false);
  const [selectedStockToMove, setSelectedStockToMove] = useState<Stock | null>(null);
  const [dashboardStocks, setDashboardStocks] = useState<Set<string>>(new Set());
  const [expandedStocks, setExpandedStocks] = useState<Set<string>>(new Set());
  
  // 使用 StockDataContext
  const { fullStocks, isLoading: stocksLoading, loadStocks, searchStocks } = useStockData();
  const [stockSearchInput, setStockSearchInput] = useState('');
  const [filteredStocks, setFilteredStocks] = useState<Stock[]>([]);

  // Use real watchlist data from context, with empty fallback
  const watchlistGroups: WatchlistGroup[] = state.watchlist?.groups || [];
  const ungroupedStockReferences = state.watchlist?.ungroupedStockReferences || [];
  
  // Convert stock references to full stock data using StockDataContext
  const { getStockBySymbol } = useStockData();
  
  const ungroupedStocks: Stock[] = ungroupedStockReferences
    .map(ref => getStockBySymbol(ref.symbol))
    .filter((stock): stock is Stock => stock !== null);

  // 當開啟新增對話框時，確保股票資料已載入
  useEffect(() => {
    if (openAddDialog && fullStocks.length === 0) {
      loadStocks();
    }
  }, [openAddDialog, fullStocks.length, loadStocks]);

  // 搜尋股票功能 - 使用 StockDataContext 的搜尋功能
  useEffect(() => {
    const results = searchStocks(stockSearchInput, stockSearchInput.trim() === '' ? 100 : 50);
    setFilteredStocks(results);
  }, [stockSearchInput, searchStocks]);

  // 載入儀表板股票狀態
  useEffect(() => {
    const loadDashboardStocks = async () => {
      if (state.currentAccountId) {
        try {
          const dashboardRefs = await WatchlistService.getDashboardStocks(state.currentAccountId);
          const dashboardSymbols = new Set(dashboardRefs.map(ref => ref.symbol));
          setDashboardStocks(dashboardSymbols);
        } catch (error) {
          console.error('Failed to load dashboard stocks:', error);
        }
      }
    };
    
    loadDashboardStocks();
  }, [state.currentAccountId, state.watchlist]); // 當關注清單變化時也重新載入


  const handleAddStock = async () => {
    if (!selectedStock || !selectedGroup) {
      showWarning('請選擇股票和分組');
      return;
    }

    if (!state.currentUser && !state.currentAccountId) {
      showError('請先登入');
      return;
    }

    const userId = state.currentAccountId || state.currentUser?.id;
    if (!userId) {
      showError('無法確定用戶身份，請重新登入');
      return;
    }

    try {
      if (selectedGroup === '未分類') {
        // Add to ungrouped stocks
        await WatchlistService.addStockReference(userId, {
          symbol: selectedStock.symbol,
          name: StockNameManager.getStockName(selectedStock.symbol)
        });
      } else {
        // Find the group ID by name
        const targetGroup = watchlistGroups.find(g => g.name === selectedGroup);
        if (!targetGroup) {
          showError('找不到指定的分組');
          return;
        }

        await WatchlistService.addStockReferenceToGroup(userId, targetGroup.id, {
          symbol: selectedStock.symbol,
          name: StockNameManager.getStockName(selectedStock.symbol)
        });
      }
      
      showSuccess(`已成功加入 ${selectedStock.symbol} 到${selectedGroup === '未分類' ? '未分類' : `分組「${selectedGroup}」`}`);
      
      // Close dialog and reset form
      setOpenAddDialog(false);
      setSelectedStock(null);
      setSelectedGroup('');
      
    } catch (error) {
      console.error('Error adding stock:', error);
      const errorMessage = error instanceof Error ? error.message : '新增股票失敗，請稍後再試';
      showError(`新增失敗: ${errorMessage}`);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      showWarning('請輸入分組名稱');
      return;
    }

    if (!state.currentUser && !state.currentAccountId) {
      showError('請先登入');
      return;
    }

    const userId = state.currentAccountId || state.currentUser?.id;
    if (!userId) {
      showError('無法確定用戶身份，請重新登入');
      return;
    }

    try {
      // Generate a random color for the new group
      const colors = ['#2196f3', '#ff9800', '#4caf50', '#f44336', '#9c27b0', '#00bcd4', '#ff5722', '#795548'];
      const randomColor = colors[Math.floor(Math.random() * colors.length)];
      
      const groupId = await WatchlistService.createGroup(userId, newGroupName.trim(), randomColor);
      
      // If we have a selected stock, add it to the new group
      if (selectedStock) {
        await WatchlistService.addStockReferenceToGroup(userId, groupId, {
          symbol: selectedStock.symbol,
          name: StockNameManager.getStockName(selectedStock.symbol)
        });
      }
      
      // Close dialog and reset form
      setOpenAddDialog(false);
      setNewGroupName('');
      setSelectedStock(null);
      setSelectedGroup('');
      
    } catch (error) {
      console.error('Error creating group:', error);
      const errorMessage = error instanceof Error ? error.message : '建立分組失敗，請稍後再試';
      showError(`建立失敗: ${errorMessage}`);
    }
  };

  const handleGroupMenuClick = (event: React.MouseEvent<HTMLElement>, groupId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedGroupForAction(groupId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedGroupForAction(null);
    setSelectedStockForAction(null);
  };

  const handleDeleteGroup = () => {
    if (selectedGroupForAction) {
      const group = watchlistGroups.find(g => g.id === selectedGroupForAction);
      setConfirmMessage(`確定要刪除分組 "${group?.name}" 嗎？分組內的股票會移到未分類。`);
      setConfirmAction('deleteGroup');
      setOpenConfirmDialog(true);
    }
    handleMenuClose();
  };

  const handleConfirmAction = async () => {
    if (!state.currentUser && !state.currentAccountId) {
      showError('請先登入');
      handleCloseConfirmDialog();
      return;
    }

    // Use current account ID for trading accounts, or user ID for fallback
    const userId = state.currentAccountId || state.currentUser?.id;
    if (!userId) {
      showError('無法確定用戶身份，請重新登入');
      handleCloseConfirmDialog();
      return;
    }

    try {
      if (confirmAction === 'deleteGroup' && selectedGroupForAction) {
        await WatchlistService.deleteGroup(userId, selectedGroupForAction);
        
      } else if (confirmAction === 'removeStock' && selectedStockForAction) {
        const { stock, groupId } = selectedStockForAction;
        
        if (groupId === null) {
          // Remove from ungrouped stocks
          await WatchlistService.removeStockReference(userId, stock.symbol);
        } else {
          // Remove from specific group
          await WatchlistService.removeStockReferenceFromGroup(userId, groupId, stock.symbol);
        }
      }
    } catch (error) {
      console.error('Error performing watchlist operation:', error);
      
      // Show user-friendly error message
      const errorMessage = error instanceof Error ? error.message : '操作失敗，請稍後再試';
      showError(`操作失敗: ${errorMessage}`);
    }
    
    handleCloseConfirmDialog();
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setConfirmAction(null);
    setConfirmMessage('');
    setSelectedGroupForAction(null);
    setSelectedStockForAction(null);
  };

  // 分組重命名相關函數
  const handleStartEditGroupName = (groupId: string, currentName: string) => {
    setEditingGroupId(groupId);
    setTempGroupName(currentName);
  };

  const handleCancelEditGroupName = () => {
    setEditingGroupId(null);
    setTempGroupName('');
  };

  const handleSaveGroupName = async (groupId: string) => {
    if (!tempGroupName.trim()) {
      showWarning('分組名稱不能為空');
      return;
    }

    if (!state.currentUser && !state.currentAccountId) {
      showError('請先登入');
      return;
    }

    const userId = state.currentAccountId || state.currentUser?.id;
    if (!userId) {
      showError('無法確定用戶身份，請重新登入');
      return;
    }

    try {
      await WatchlistService.updateGroupName(userId, groupId, tempGroupName.trim());
      setEditingGroupId(null);
      setTempGroupName('');
    } catch (error) {
      console.error('Error updating group name:', error);
      const errorMessage = error instanceof Error ? error.message : '更新分組名稱失敗，請稍後再試';
      showError(`更新失敗: ${errorMessage}`);
    }
  };

  // 直接刪除股票的函數（編輯模式下不需要確認對話框）
  const handleDirectRemoveStock = async (stock: Stock, groupId: string | null) => {
    if (!state.currentUser && !state.currentAccountId) {
      showError('請先登入');
      return;
    }

    const userId = state.currentAccountId || state.currentUser?.id;
    if (!userId) {
      showError('無法確定用戶身份，請重新登入');
      return;
    }

    try {
      if (groupId === null) {
        // Remove from ungrouped stocks
        await WatchlistService.removeStockReference(userId, stock.symbol);
      } else {
        // Remove from specific group
        await WatchlistService.removeStockReferenceFromGroup(userId, groupId, stock.symbol);
      }
    } catch (error) {
      console.error('Error removing stock:', error);
      const errorMessage = error instanceof Error ? error.message : '移除股票失敗，請稍後再試';
      showError(`移除失敗: ${errorMessage}`);
    }
  };

  // 處理移動股票到分組
  const handleMoveStockClick = (stock: Stock) => {
    setSelectedStockToMove(stock);
    setMoveStockDialogOpen(true);
  };

  // 處理移動股票確認
  const handleMoveStockConfirm = async (groupId: string | null, newGroupName?: string) => {
    if (!selectedStockToMove || !state.currentAccountId) {
      return;
    }

    try {
      const stockRef = {
        symbol: selectedStockToMove.symbol,
        name: StockNameManager.getStockName(selectedStockToMove.symbol),
      };

      if (newGroupName) {
        // 創建新分組並移動股票
        const colors = ['#2196f3', '#ff9800', '#4caf50', '#f44336', '#9c27b0', '#00bcd4', '#ff5722', '#795548'];
        const randomColor = colors[Math.floor(Math.random() * colors.length)];
        const newGroupId = await WatchlistService.createGroup(state.currentAccountId, newGroupName, randomColor);
        await WatchlistService.moveStockReference(state.currentAccountId, stockRef, null, newGroupId);
        showSuccess(`已建立分組「${newGroupName}」並移動 ${selectedStockToMove.symbol}`);
      } else if (groupId === null) {
        // 這個情況不應該發生，因為從未分類移動到未分類沒有意義
        showWarning('無法移動到相同位置');
        return;
      } else {
        // 移動到指定分組
        const targetGroup = watchlistGroups.find(g => g.id === groupId);
        await WatchlistService.moveStockReference(state.currentAccountId, stockRef, null, groupId);
        showSuccess(`已將 ${selectedStockToMove.symbol} 移動到分組「${targetGroup?.name}」`);
      }
    } catch (error) {
      console.error('Move stock error:', error);
      const errorMessage = error instanceof Error ? error.message : '移動股票失敗，請稍後再試';
      showError(`移動失敗: ${errorMessage}`);
    }

    setSelectedStockToMove(null);
  };



  // 處理股利資訊展開/收合
  const handleExpandToggle = (stockSymbol: string) => {
    setExpandedStocks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stockSymbol)) {
        newSet.delete(stockSymbol);
      } else {
        newSet.add(stockSymbol);
      }
      return newSet;
    });
  };

  // 处理个别股票的仪表板显示切换
  const handleToggleStockDashboard = async (stock: Stock) => {
    if (!state.currentAccountId) {
      showError('请先登入');
      return;
    }

    const isInDashboard = dashboardStocks.has(stock.symbol);
    
    try {
      if (isInDashboard) {
        // 从仪表板移除
        await WatchlistService.removeDashboardStock(state.currentAccountId, stock.symbol);
        setDashboardStocks(prev => {
          const newSet = new Set(prev);
          newSet.delete(stock.symbol);
          return newSet;
        });
        showSuccess(`已将 ${stock.symbol} 从仪表板移除`);
      } else {
        // 添加到仪表板
        await WatchlistService.addDashboardStock(state.currentAccountId, {
          symbol: stock.symbol,
          name: StockNameManager.getStockName(stock.symbol)
        });
        setDashboardStocks(prev => {
          const newSet = new Set(prev).add(stock.symbol);
          return newSet;
        });
        showSuccess(`已将 ${stock.symbol} 加入仪表板显示`);
      }
      
      // 更新全局状态中的 dashboardStocks(ARCH-06:經由語意化方法)
      const updatedWatchlist = await WatchlistService.getUserWatchlist(state.currentAccountId);
      updateWatchlistData(updatedWatchlist);
      
    } catch (error) {
      console.error('❌ Watchlist Toggle stock dashboard error:', error);
      const errorMessage = error instanceof Error ? error.message : '设定股票仪表板显示失败，请稍后再试';
      showError(`设定失败: ${errorMessage}`);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <PageHeader
        title="關注清單"
        icon={Visibility}
        subtitle={`管理您的股票關注清單 • ${watchlistGroups.length}個分組`}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant={isEditMode ? "contained" : "outlined"}
              startIcon={isEditMode ? <CheckIcon /> : <EditIcon />}
              onClick={() => setIsEditMode(!isEditMode)}
              color={isEditMode ? "success" : "primary"}
            >
              {isEditMode ? '完成編輯' : '編輯模式'}
            </Button>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => setOpenAddDialog(true)}
            >
              新增股票
            </Button>
          </Box>
        }
      />

      {watchlistGroups.map((group) => (
        <Accordion key={group.id} defaultExpanded sx={{ mb: 1.5 }}>
          <Box sx={{ position: 'relative' }}>
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />} 
              sx={{ 
                backgroundColor: `${group.color}20`, 
                borderLeft: `5px solid ${group.color}`,
                minHeight: { xs: 48, sm: 56 },
                '& .MuiAccordionSummary-content': {
                  margin: { xs: '8px 0', sm: '12px 0' }
                },
                pr: 6 // 為右上角按鈕預留空間
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                <Chip 
                  label={group.stockReferences?.length || 0} 
                  size="small"
                  sx={{ 
                    backgroundColor: group.color, 
                    color: 'white',
                    fontSize: { xs: '0.7rem', sm: '0.75rem' }
                  }} 
                />
                {/* 可編輯的分組名稱 */}
                {editingGroupId === group.id ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <TextField
                      value={tempGroupName}
                      onChange={(e) => setTempGroupName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveGroupName(group.id);
                        } else if (e.key === 'Escape') {
                          handleCancelEditGroupName();
                        }
                      }}
                      size="small"
                      autoFocus
                      sx={{ 
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                          fontWeight: 'bold'
                        }
                      }}
                    />
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSaveGroupName(group.id);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        cursor: 'pointer',
                        color: 'success.main',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: 'success.light',
                          transform: 'scale(1.1)'
                        }
                      }}
                    >
                      <CheckIcon fontSize="small" />
                    </Box>
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelEditGroupName();
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        cursor: 'pointer',
                        color: 'text.secondary',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          transform: 'scale(1.1)'
                        }
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Typography 
                      variant="h6" 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                    >
                      {group.name}
                    </Typography>
                    {/* 編輯按鈕 - 只在編輯模式下顯示 */}
                    {isEditMode && (
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartEditGroupName(group.id, group.name);
                        }}
                        sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          cursor: 'pointer',
                          color: 'text.secondary',
                          transition: 'all 0.2s',
                          '&:hover': {
                            color: 'primary.main',
                            backgroundColor: 'action.hover'
                          }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </AccordionSummary>
            {/* 分組選項按鈕 - 只在編輯模式下顯示 */}
            {isEditMode && (
              <Tooltip title="分組選項">
                <IconButton 
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGroupMenuClick(e, group.id);
                  }}
                  sx={{ 
                    position: 'absolute',
                    top: '50%',
                    right: 8,
                    transform: 'translateY(-50%)',
                    zIndex: 2
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
            <Box sx={{ 
              display: 'grid',
              gap: { xs: 1, sm: 1.5 },
              gridTemplateColumns: { 
                xs: '1fr', 
                sm: 'repeat(2, 1fr)', 
                md: 'repeat(2, 1fr)', 
                lg: 'repeat(3, 1fr)', 
                xl: 'repeat(4, 1fr)' 
              }
            }}>
              {group.stockReferences.map((stockRef) => {
                // Convert stock reference to full stock data
                const stock = getStockBySymbol(stockRef.symbol);
                
                if (!stock) return null;
                
                return (
                <Box
                  key={stock.symbol}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    p: { xs: 2, sm: 2.5 },
                    transition: 'all 0.2s ease-in-out',
                    position: 'relative',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  {/* Remove Button - 只在編輯模式下顯示 */}
                  {isEditMode && (
                    <Tooltip title="移除股票">
                      <IconButton 
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDirectRemoveStock(stock, group.id);
                        }}
                        sx={{ 
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          zIndex: 1,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          '&:hover': {
                            backgroundColor: '#ffebee',
                            color: '#d32f2f'
                          }
                        }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  {/* Header Row */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box>
                      <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        color="primary" 
                        sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
                      >
                        {stock.symbol}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        color="text.secondary"
                        sx={{ mt: 0.2 }}
                      >
                        {StockNameManager.getStockName(stock.symbol)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                      <Typography 
                        variant="h5" 
                        fontWeight="bold" 
                        sx={{ 
                          color: '#2c3e50',
                        }}
                      >
                        ${stock.currentPrice}
                      </Typography>
                      
                      {/* Action Buttons - 在價格下方 */}
                      <Box sx={{ 
                        display: 'flex',
                        gap: 0.5
                      }}>
                        {/* Dashboard Toggle Button */}
                        <Tooltip title={dashboardStocks.has(stock.symbol) ? '從儀表板移除' : '加入儀表板顯示'}>
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleStockDashboard(stock);
                            }}
                            sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              cursor: 'pointer',
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              border: '1px solid #e0e0e0',
                              color: dashboardStocks.has(stock.symbol) ? '#ff9800' : 'text.secondary',
                              transition: 'all 0.2s',
                              '&:hover': {
                                color: '#ff9800',
                                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                                borderColor: '#ff9800',
                                transform: 'scale(1.1)'
                              }
                            }}
                          >
                            {dashboardStocks.has(stock.symbol) ? (
                              <DashboardIcon fontSize="small" />
                            ) : (
                              <DashboardOutlinedIcon fontSize="small" />
                            )}
                          </Box>
                        </Tooltip>
                        
                        {/* Dividend Button */}
                        <Tooltip title="查看股利資訊">
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExpandToggle(stock.symbol);
                            }}
                            sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              cursor: 'pointer',
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              border: '1px solid #e0e0e0',
                              color: expandedStocks.has(stock.symbol) ? '#1976d2' : 'text.secondary',
                              transition: 'all 0.2s',
                              '&:hover': {
                                color: '#1976d2',
                                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                borderColor: '#1976d2',
                                transform: 'scale(1.1)'
                              }
                            }}
                          >
                            <Assessment fontSize="small" />
                          </Box>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>

                  {/* Change Row */}
                  {(() => {
                    const changeDirection = getTrendDirection(stock.change);
                    const TrendIconComponent = changeDirection === 'down' ? TrendingDown : TrendingUp;
                    const changeColor = getTrendTextColor(stock.change);
                    const chipBaseProps = getTrendChipProps(stock.changePercent);
                    const { sx: chipBaseSx, variant: chipBaseVariant, ...chipRest } = chipBaseProps;
                    const chipVariant = chipBaseVariant ?? (Math.abs(stock.changePercent) > 2 ? 'filled' : 'outlined');
                    return (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TrendIconComponent sx={{ fontSize: { xs: 16, sm: 18 }, color: changeColor }} />
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            sx={{ color: changeColor }}
                          >
                            {stock.change >= 0 ? '+' : ''}{stock.change}
                          </Typography>
                        </Box>
                        
                        <Chip
                          {...chipRest}
                          label={`${stock.changePercent >= 0 ? '+' : ''}${stock.changePercent.toFixed(2)}%`}
                          variant={chipVariant}
                          sx={{ 
                            fontWeight: 'bold',
                            ...(chipBaseSx || {})
                          }}
                        />
                      </Box>
                    );
                  })()}
                  
                  {/* Dividend Display - 可展開區域 */}
                  {expandedStocks.has(stock.symbol) && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                      <DividendDisplay
                        stockSymbol={stock.symbol}
                        stockName={StockNameManager.getStockName(stock.symbol)}
                        currentPrice={stock.currentPrice}
                        showTitle={false}
                        maxRecords={5}
                        compact={true}
                      />
                    </Box>
                  )}
                </Box>
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      <Accordion defaultExpanded sx={{ mb: 1.5 }}>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />} 
          sx={{ 
            backgroundColor: '#f5f5f5',
            minHeight: { xs: 48, sm: 56 }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip 
              label={ungroupedStocks.length} 
              size="small" 
              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
            />
            <Typography 
              variant="h6" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              未分類
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <List dense sx={{ width: '100%' }}>
            {ungroupedStocks.map((stock, index) => (
              <React.Fragment key={stock.symbol}>
                <ListItem sx={{ py: { xs: 0.5, sm: 1 }, px: 2 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography 
                              variant="subtitle1" 
                              fontWeight="bold"
                              sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, lineHeight: 1.2 }}
                            >
                              {stock.symbol}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, lineHeight: 1.2 }}
                            >
                              {StockNameManager.getStockName(stock.symbol)}
                            </Typography>
                          </Box>
                          {/* 編輯模式下的按鈕組 */}
                          {isEditMode && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="移動到分組">
                                <IconButton 
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMoveStockClick(stock);
                                  }}
                                  sx={{
                                    color: 'text.secondary',
                                    '&:hover': {
                                      color: '#1976d2',
                                      backgroundColor: '#e3f2fd'
                                    }
                                  }}
                                >
                                  <MoveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="移除股票">
                                <IconButton 
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDirectRemoveStock(stock, null);
                                  }}
                                  sx={{
                                    color: 'text.secondary',
                                    '&:hover': {
                                      color: '#d32f2f',
                                      backgroundColor: '#ffebee'
                                    }
                                  }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                        {(() => {
                          const changeDirection = getTrendDirection(stock.change);
                          const TrendIconComponent = changeDirection === 'down' ? TrendingDown : TrendingUp;
                          const changeColor = getTrendTextColor(stock.change);
                          const chipBaseProps = getTrendChipProps(stock.changePercent);
                          const { sx: chipBaseSx, variant: chipBaseVariant, ...chipRest } = chipBaseProps;
                          const chipVariant = chipBaseVariant ?? 'outlined';
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography 
                                variant="h6" 
                                fontWeight="bold"
                                sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
                              >
                                ${stock.currentPrice}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TrendIconComponent sx={{ fontSize: 14, color: changeColor }} />
                                <Chip
                                  {...chipRest}
                                  label={`${stock.change >= 0 ? '+' : ''}${stock.change} (${stock.changePercent.toFixed(2)}%)`}
                                  size="small"
                                  variant={chipVariant}
                                  sx={{ 
                                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                    height: { xs: 20, sm: 24 },
                                    ...(chipBaseSx || {})
                                  }}
                                />
                              </Box>
                            </Box>
                          );
                        })()}
                      </Box>
                    }
                  />
                </ListItem>
                {index < ungroupedStocks.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Add Stock Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>新增股票到關注清單</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <Autocomplete
              fullWidth
              options={filteredStocks}
              getOptionLabel={(option) => `${option.symbol} - ${StockNameManager.getStockName(option.symbol)}`}
              value={selectedStock}
              onChange={(event, newValue) => setSelectedStock(newValue)}
              onInputChange={(event, newInputValue) => {
                setStockSearchInput(newInputValue);
              }}
              filterOptions={(options) => options} // 不進行額外過濾，因為我們已經在 useEffect 中處理
              loading={stocksLoading}
              loadingText="正在載入股票資料..."
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="搜尋股票"
                  placeholder="輸入股票代號或名稱..."
                  helperText={`可搜尋所有台股 (${fullStocks.length > 0 ? `共 ${fullStocks.length} 支` : '載入中...'})`}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {stocksLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                // Extract the key from props to pass it directly
                const { key, ...otherProps } = props;
                return (
                  <Box component="li" key={key} {...otherProps}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        {option.symbol}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {StockNameManager.getStockName(option.symbol)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        現價: ${option.currentPrice} ({option.changePercent >= 0 ? '+' : ''}{option.changePercent.toFixed(2)}%)
                      </Typography>
                    </Box>
                  </Box>
                );
              }}
              noOptionsText={stocksLoading ? "正在載入股票資料..." : "找不到符合的股票"}
            />
            
            <FormControl fullWidth>
              <InputLabel>選擇分組</InputLabel>
              <Select
                value={selectedGroup}
                label="選擇分組"
                onChange={(e) => setSelectedGroup(e.target.value)}
              >
                <MenuItem value="未分類">未分類</MenuItem>
                {watchlistGroups.map((group) => (
                  <MenuItem key={group.id} value={group.name}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ my: 1 }}>或</Divider>

            <TextField
              fullWidth
              label="建立新分組"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="輸入新分組名稱..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>取消</Button>
          <Button
            variant="contained"
            // UI-09:按鈕行為與文案一致 — 同時選了股票+分組又輸入新分組名時,
            // 舊版文案顯示「建立分組」但實際執行 handleAddStock(加入既有分組)
            onClick={newGroupName.trim() ? handleCreateGroup : handleAddStock}
            disabled={(!selectedStock && !newGroupName.trim()) || (!!selectedStock && !newGroupName.trim() && !selectedGroup)}
          >
            {newGroupName.trim() ? '建立分組' : '加入關注'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move Stock Dialog */}
      <GroupSelectorDialog
        open={moveStockDialogOpen}
        onClose={() => {
          setMoveStockDialogOpen(false);
          setSelectedStockToMove(null);
        }}
        onConfirm={handleMoveStockConfirm}
        title="移動股票到分組"
        groups={watchlistGroups}
        stockName={selectedStockToMove?.name}
        stockSymbol={selectedStockToMove?.symbol}
        showUngroupedOption={false}
        confirmButtonText="移動"
      />

      {/* Context Menu - 只保留分組刪除功能 */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {selectedGroupForAction && (
          <MenuItem onClick={handleDeleteGroup}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <Typography color="error">刪除分組</Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>確認操作</DialogTitle>
        <DialogContent>
          <Typography>{confirmMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog}>取消</Button>
          <Button 
            onClick={handleConfirmAction}
            color="error" 
            variant="contained"
          >
            確定
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Watchlist;

